// Parser for the literals gno.land's `qeval` returns.
//
// evaluateExpression(pkgPath, expr) answers with one line per returned value,
// each of the form `(<value> <type>)`, for example:
//
//   (236091177331 int64)
//   (-5 int64)
//   (true bool)
//   ("he said \"hi\"" string)
//   ("test1" string)\n(true bool)        <- a two-value return, newline-joined
//   (nil *gno.land/r/sys/users.UserData)
//   (slice[(1 int64),(2 int64),(3 int64)] []int64)   <- a slice, seen live on pearl-1
//   (slice[] []int64)                                <- an empty one
//
// Strings are Go-quoted (strconv.Quote), so the value may contain spaces and
// the type never does; the parser therefore reads a quoted string to its
// closing quote and takes everything after it as the type. A slice is read to
// its matching bracket (element strings may hold brackets and commas, so the
// scan honours quotes), and its elements are parsed as typed values in turn.
// Anything else splits at the first space. Integers come back as BigInt so
// that token-scale int64s never silently lose precision.

const INT_TYPES = new Set(['int', 'int8', 'int16', 'int32', 'int64', 'uint', 'uint8', 'uint16', 'uint32', 'uint64']);
const FLOAT_TYPES = new Set(['float32', 'float64']);

// Decodes the body of a Go double-quoted string (without the quotes).
export function unquoteGo(body) {
  let out = '';
  for (let i = 0; i < body.length; i++) {
    const c = body[i];
    if (c !== '\\') { out += c; continue; }
    const e = body[++i];
    switch (e) {
      case 'n': out += '\n'; break;
      case 't': out += '\t'; break;
      case 'r': out += '\r'; break;
      case 'a': out += '\x07'; break;
      case 'b': out += '\b'; break;
      case 'f': out += '\f'; break;
      case 'v': out += '\v'; break;
      case '\\': out += '\\'; break;
      case '"': out += '"'; break;
      case "'": out += "'"; break;
      case 'x': out += String.fromCharCode(parseInt(body.slice(i + 1, i + 3), 16)); i += 2; break;
      case 'u': out += String.fromCodePoint(parseInt(body.slice(i + 1, i + 5), 16)); i += 4; break;
      case 'U': out += String.fromCodePoint(parseInt(body.slice(i + 1, i + 9), 16)); i += 8; break;
      default:
        if (e >= '0' && e <= '7') { out += String.fromCharCode(parseInt(body.slice(i, i + 3), 8)); i += 2; break; }
        throw new Error(`gnolit: bad escape \\${e}`);
    }
  }
  return out;
}

// Finds the index of the closing quote of a Go-quoted string starting at
// s[start] === '"'. Returns -1 if unterminated.
function closingQuote(s, start) {
  for (let i = start + 1; i < s.length; i++) {
    if (s[i] === '\\') { i++; continue; }
    if (s[i] === '"') return i;
  }
  return -1;
}

// Finds the index of the `]` closing the `[` at s[start], skipping quoted
// strings and nested brackets/parens. Returns -1 if unterminated.
function closingBracket(s, start) {
  let depth = 0;
  for (let i = start; i < s.length; i++) {
    const c = s[i];
    if (c === '"') { i = closingQuote(s, i); if (i < 0) return -1; continue; }
    if (c === '[' || c === '(') depth++;
    else if (c === ']' || c === ')') { depth--; if (depth === 0) return i; }
  }
  return -1;
}

// Finds the index of the `}` closing the `{` at s[start], skipping quoted
// strings. Tracks only brace depth -- a struct's fields are each a balanced
// `(value type)` pair, so braces never need to see parens or square brackets
// to find their own match. Returns -1 if unterminated.
function closingBrace(s, start) {
  let depth = 0;
  for (let i = start; i < s.length; i++) {
    const c = s[i];
    if (c === '"') { i = closingQuote(s, i); if (i < 0) return -1; continue; }
    if (c === '{') depth++;
    else if (c === '}') { depth--; if (depth === 0) return i; }
  }
  return -1;
}

// Splits `slice[...]`'s body at top-level commas, honouring quotes and nesting.
function splitElements(body) {
  const out = [];
  let depth = 0, cur = '';
  for (let i = 0; i < body.length; i++) {
    const c = body[i];
    if (c === '"') {
      const end = closingQuote(body, i);
      if (end < 0) throw new Error(`gnolit: unterminated string in slice: ${body}`);
      cur += body.slice(i, end + 1); i = end; continue;
    }
    if (c === '[' || c === '(') depth++;
    else if (c === ']' || c === ')') depth--;
    if (c === ',' && depth === 0) { out.push(cur); cur = ''; continue; }
    cur += c;
  }
  if (cur.trim() !== '') out.push(cur);
  return out;
}

// Parses one `(<value> <type>)` line into { type, value, raw }.
export function parseGnoValue(line) {
  const t = line.trim();
  if (!t.startsWith('(') || !t.endsWith(')')) throw new Error(`gnolit: not a typed value: ${line}`);
  const inner = t.slice(1, -1);
  let raw, type;
  if (inner.startsWith('"')) {
    const end = closingQuote(inner, 0);
    if (end < 0) throw new Error(`gnolit: unterminated string: ${line}`);
    raw = inner.slice(0, end + 1);
    type = inner.slice(end + 1).trim();
  } else if (inner.startsWith('slice[')) {
    const end = closingBracket(inner, 5);
    if (end < 0) throw new Error(`gnolit: unterminated slice: ${line}`);
    raw = inner.slice(0, end + 1);
    type = inner.slice(end + 1).trim();
  } else if (inner.startsWith('struct{')) {
    // RecentResults' []Result -- a slice of structs -- is the first place
    // this project has needed to read one. A naive first-space split (what
    // the final `else` branch below does) breaks on a struct: its first
    // field is itself a "(value type)" pair, so the first space in the
    // whole string sits INSIDE that field, long before the struct's own
    // closing `}` and its type name. The brace has to be matched properly.
    const end = closingBrace(inner, 6);
    if (end < 0) throw new Error(`gnolit: unterminated struct: ${line}`);
    raw = inner.slice(0, end + 1);
    type = inner.slice(end + 1).trim();
  } else {
    const sp = inner.indexOf(' ');
    if (sp < 0) throw new Error(`gnolit: no type: ${line}`);
    raw = inner.slice(0, sp);
    type = inner.slice(sp + 1).trim();
  }
  if (!type) throw new Error(`gnolit: empty type: ${line}`);
  return { type, raw, value: convert(raw, type) };
}

function convert(raw, type) {
  if (raw === 'nil') return type.startsWith('[]') ? [] : null; // a nil slice is an empty one to a caller
  if (INT_TYPES.has(type)) return BigInt(raw);
  if (FLOAT_TYPES.has(type)) return Number(raw);
  if (type === 'bool') return raw === 'true';
  if (raw.startsWith('"')) return unquoteGo(raw.slice(1, -1));
  if (raw.startsWith('slice[')) return splitElements(raw.slice(6, -1)).map((e) => parseGnoValue(e).value);
  // struct{(v1 t1),(v2 t2),...} -> the field values, IN DECLARATION ORDER.
  // The VM's printer never names fields here (see values_string_stream.go's
  // StructValue.WriteProtected) -- there is no key to read, only position --
  // so a caller that wants named fields (reads.js's recentResults, for
  // Result{DuelID, Round, Winner, Loser, Rung, Height}) has to already know
  // that order from the realm's own source and zip it on afterwards.
  // splitElements is reused unchanged: it tracks only paren/bracket depth,
  // which is exactly what is needed to split a struct's fields too, since
  // each field is its own balanced (value type) pair and the braces around
  // the whole thing never participate in that depth count.
  if (raw.startsWith('struct{')) return splitElements(raw.slice(7, -1)).map((e) => parseGnoValue(e).value);
  return raw; // addresses and anything else print bare
}

// Parses a whole qeval answer: one value per non-empty line.
export function parseGnoValues(text) {
  return String(text).split('\n').filter((l) => l.trim() !== '').map(parseGnoValue);
}

// Just the JS values, in order.
export function gnoValues(text) {
  return parseGnoValues(text).map((v) => v.value);
}

// The single value of a one-value answer.
export function gnoValue(text) {
  const vs = parseGnoValues(text);
  if (vs.length !== 1) throw new Error(`gnolit: expected one value, got ${vs.length}`);
  return vs[0].value;
}
